import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import { getEdgeCenter, getSmoothStepPath } from '@xyflow/react';
import classnames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import config from '../../app/config';
import { color } from '../../app/theme';
import {
  GATriggerType,
  ModalType,
  NodeType,
  PlatformType,
} from '../../app/types';
import { checkFeatures } from '../../app/utils';
import IconChevronRight from '../../assets/images/Icons/IconChevronRight';
import addHoverImage from '../../assets/images/png/img-ma-add-hover.png';
import addImage from '../../assets/images/png/img-ma-add.png';
import triangleImage from '../../assets/images/png/img-ma-triangle.png';
import {
  addAnyConditionsNode,
  addCouponNode,
  addCustomerConditionsNode,
  addEmojiConditionsNode,
  addGAViewConditionsNode,
  addInboxNode,
  addKeywordConditionsNode,
  addMANode,
  addMessageNode,
  addScheduleNode,
  addTagNode,
  addWaitNode,
  checkPlatform,
  getMaxFlowCount,
  setDataByNodeId,
} from '../../features/mainSlice';
import { openModal } from '../../features/modalSlice';

const useStyles = makeStyles(
  {
    root: {
      position: 'absolute',
    },
    btn: {
      width: 32,
      height: 32,
      cursor: 'pointer',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      backgroundImage: `url(${addImage})`,
      '&:hover': {
        backgroundImage: `url(${addHoverImage})`,
      },
    },
    selectBlock: {
      position: 'relative',
      top: '-38px',
      right: '-42px',
      display: 'flex',
      alignItems: 'start',
    },
    triangle: {
      height: 44,
      minWidth: 7,
      backgroundImage: `url(${triangleImage})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
    },
    select: {
      padding: '4px 4px 4px 12px',
      borderRadius: '12px',
      background: color.$7,
      '& > div:hover': {
        color: color.$2,
      },
    },
    row: {
      padding: '9px 0',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
    },
    none: {
      display: 'none',
    },
    subMenu: {
      position: 'absolute',
      top: '0',
      left: 'calc(100% + 4px)',
      padding: '4px 12px',
      background: color.$7,
      borderRadius: '12px',
      '& > div:hover': {
        color: color.$2,
      },
    },
    disabled: {
      cursor: 'default',
      color: `${color.$10} !important`,
    },
    selected: {
      color: `${color.$2} !important`,
    },
  },
  {
    name: 'AddEdge',
  },
);

const foreignObjectSize = 32;

const Item = forwardRef(
  ({ text, index, showSubMenu, setShowSubMenu, menuItem }, ref) => {
    const classes = useStyles();

    return (
      <div
        style={{
          position: 'relative',
        }}
      >
        <div
          className={classnames(classes.row, {
            [classes.selected]: showSubMenu,
          })}
          onMouseEnter={() => {
            setShowSubMenu(true, index);
          }}
        >
          {text}
          <IconChevronRight
            id={uuidv4()}
            iconColor={showSubMenu ? color.$2 : color.$3}
            style={{ position: 'initial' }}
          />
        </div>
        {showSubMenu && (
          <div
            ref={ref}
            className={classes.subMenu}
            onMouseLeave={() => {
              setShowSubMenu(false, index);
            }}
          >
            {menuItem.map((item, i) => {
              return (
                <div
                  key={`${text} - ${i}`}
                  className={classnames(classes.row, {
                    [classes.disabled]: item.disabled,
                  })}
                  onClick={() => {
                    if (item?.onClick && !item?.disabled) item?.onClick();
                  }}
                >
                  {item.text}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

const AddEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  source,
  target,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectBlockRef = useRef(null);
  const subMenuRef = useRef([]);

  const [isShowSelect, setIsShowSelect] = useState(false);
  const [isShowSubMenuIndex, setIsShowSubMenuIndex] = useState(null);
  const [foreignObjectDimensions, setForeignObjectDimensions] = useState({
    width: foreignObjectSize,
    height: foreignObjectSize,
  });

  const [subMenuDimensions, setSubMenuDimensions] = useState({
    width: 0,
    height: 0,
  });

  const { serviceFeatures, integration, features } = useSelector(
    store => store.organization,
  );
  const { couponList } = useSelector(state => state.superMarket);
  const { isActive, isPreview, data, list } = useSelector(store => store.ma);
  const { nodes, platform } = data;

  const isPlatformVerify = checkPlatform(platform, integration);
  const sourceNode = (nodes || []).find(n => n?.id === source);
  const targetNode = (nodes || []).find(n => n?.id === target);
  const isMessage = sourceNode?.type === NodeType.MESSAGE;

  const isTag = sourceNode?.type === NodeType.TAGGING;
  const targetNodeIsTag = targetNode?.type === NodeType.TAGGING;

  const isInbox = sourceNode?.type === NodeType.INBOX;
  const targetNodeIsInbox = targetNode?.type === NodeType.INBOX;

  const isCondition = sourceNode?.type === NodeType.CONDITION;
  const targetNodeIsEnd = targetNode?.type === NodeType.END;
  const isGACondition = sourceNode?.type === NodeType.GACONDITION;
  const targetNodeIsGACondition = targetNode?.type === NodeType.GACONDITION;
  const targetNodeIsCustomerCondition =
    targetNode?.type === NodeType.CUSTOMERCONDITION;
  const targetNodeIsCondition = targetNode?.type === NodeType.CONDITION;
  const isLivechat = platform === PlatformType.LIVECHAT;
  const isLine = platform === PlatformType.LINE;

  const { enable } = features?.ma_sticker || {};
  const { enableSticker } = features?.marketing_automation || {};

  // 所有group內的condition node
  const targetGAConditionsNode =
    targetNodeIsGACondition || targetNodeIsCondition
      ? (nodes || []).filter(
          n => n?.data?.groupId === targetNode?.data?.groupId,
        )
      : [];

  // 分支時若有＋，要讓＋上面一點
  const isTopPosition =
    (targetNodeIsGACondition && targetGAConditionsNode.length >= 2) ||
    targetNodeIsCondition ||
    targetNodeIsCustomerCondition;

  const canAddCondition = useMemo(
    () =>
      (sourceNode?.data?.messages || []).some(m => {
        return (m?.data?.elements || []).some(e => {
          return (e?.buttons || []).some(button => {
            const isPhone = button?.type === 'phone';
            const isNotHttpUrl =
              button?.type === 'url' && !/(http(s?)):\/\//i.test(button?.data);
            const isLocation = button?.type === 'location';
            return !isPhone && !isNotHttpUrl && !isLocation;
          });
        });
      }),
    [sourceNode],
  );

  const endCount = useMemo(() => {
    return (nodes || []).filter(n => n?.type === NodeType.END).length || 0;
  }, [nodes.length]);
  const isMaxEndNodVerify = endCount < config.MAX_END_COUNT;

  const isMaxFlowVerify = useMemo(() => {
    const count = dispatch(getMaxFlowCount(source));
    return config.MAX_FLOW_COUNT > count;
  }, [nodes.length, source]);

  const onShow = e => {
    e.stopPropagation();
    setIsShowSelect(!isShowSelect);
  };

  const onAddMA = () => {
    dispatch(addMANode(source, target));
  };

  // 當sourceNode是“GA事件”或是“觸發條件的GA事件”且targetNode為“發送訊息”，這時若在這兩個node插入新的node，則需清除targetNode發送訊息中“autoTrackProduct”欄位將其設定為undefined
  const clearAutoTrackProduct = async () => {
    const sourceNodeIsTriggerGAEvent =
      sourceNode?.type === NodeType?.TRIGGER &&
      sourceNode?.data?.type === GATriggerType.GAADDTOCART;

    const sourceNodeIsGACondition =
      sourceNode?.type === NodeType?.GACONDITION &&
      sourceNode?.data?.type === 'trigger';

    const targetNodeIsMessage = targetNode?.type === NodeType?.MESSAGE;
    if (
      (sourceNodeIsTriggerGAEvent || sourceNodeIsGACondition) &&
      targetNodeIsMessage
    ) {
      await dispatch(
        setDataByNodeId(targetNode?.id, {
          ...targetNode?.data,
          messages: targetNode?.data?.messages?.map(msg => {
            return {
              ...msg,
              data: {
                ...msg?.data,
                autoTrackProduct: undefined,
              },
            };
          }),
        }),
      );
    }
  };

  const onAddTag = async () => {
    await clearAutoTrackProduct();
    await dispatch(addTagNode(source, target));
  };

  const onAddInbox = async () => {
    await clearAutoTrackProduct();
    await dispatch(addInboxNode(source, target));
  };

  const onAddMessage = async () => {
    await clearAutoTrackProduct();
    await dispatch(addMessageNode(source, target));
  };

  const onAddCoupon = async () => {
    await clearAutoTrackProduct();
    await dispatch(addCouponNode(source, target));
  };

  const onAddSchedule = async () => {
    await clearAutoTrackProduct();
    await dispatch(addScheduleNode(source, target));
  };

  const onAddWait = async () => {
    await clearAutoTrackProduct();
    await dispatch(addWaitNode(source, target));
  };

  const onAddConditions = () => {
    if (targetNodeIsEnd) {
      if (isMessage && canAddCondition) {
        dispatch(addAnyConditionsNode(source));
      } else if (isLine && (enable || enableSticker)) {
        dispatch(addEmojiConditionsNode(source));
      } else {
        dispatch(addKeywordConditionsNode(source));
      }
    } else {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('add-conditions-modal.title'),
            content: t('add-conditions-modal.content'),
            cancelText: t('add-conditions-modal.cancelText'),
            submitText: t('add-conditions-modal.submitText'),
          },
          onClick: () => {
            if (isMessage && canAddCondition) {
              dispatch(addAnyConditionsNode(source));
            } else if (isLine && (enable || enableSticker)) {
              dispatch(addEmojiConditionsNode(source));
            } else {
              dispatch(addKeywordConditionsNode(source));
            }
          },
        }),
      );
    }
  };

  const onAddGAConditions = () => {
    if (targetNodeIsEnd) {
      dispatch(addGAViewConditionsNode(source));
    } else {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('add-ga-conditions-modal.title'),
            content: t('add-ga-conditions-modal.content'),
            cancelText: t('add-ga-conditions-modal.cancelText'),
            submitText: t('add-ga-conditions-modal.submitText'),
          },
          onClick: () => {
            dispatch(addGAViewConditionsNode(source));
          },
        }),
      );
    }
  };

  const onAddCustomerConditions = () => {
    if (targetNodeIsEnd) {
      dispatch(addCustomerConditionsNode(source));
    } else {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('add-customer-conditions-modal.title'),
            content: t('add-customer-conditions-modal.content'),
            cancelText: t('add-customer-conditions-modal.cancelText'),
            submitText: t('add-customer-conditions-modal.submitText'),
          },
          onClick: () => {
            dispatch(addCustomerConditionsNode(source));
          },
        }),
      );
    }
  };

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const [edgeCenterX, edgeCenterY] = getEdgeCenter({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  useEffect(() => {
    if (selectBlockRef.current) {
      const { offsetWidth, offsetHeight } = selectBlockRef.current;
      setForeignObjectDimensions({
        width: offsetWidth,
        height: offsetHeight,
      });
    }
  }, [isShowSelect]);

  useEffect(() => {
    if (isShowSubMenuIndex !== null && subMenuRef.current[isShowSubMenuIndex]) {
      const currentSubMenu = subMenuRef.current[isShowSubMenuIndex];
      setSubMenuDimensions({
        width: currentSubMenu.offsetWidth,
        height: currentSubMenu.offsetHeight - 52 + 6,
      });
    } else {
      setSubMenuDimensions({
        width: 0,
        height: 0,
      });
    }
  }, [isShowSubMenuIndex]);

  return (
    <>
      <path
        id={id}
        style={{
          ...style,
          ...{
            stroke: color.$10,
          },
        }}
        className="react-flow__edge-path"
        d={edgePath}
      />
      <ClickAwayListener onClickAway={() => setIsShowSelect(false)}>
        <foreignObject
          width={
            isShowSelect
              ? foreignObjectDimensions.width + subMenuDimensions.width + 42
              : foreignObjectSize
          }
          height={
            isShowSelect
              ? foreignObjectDimensions.height + subMenuDimensions.height + 8
              : foreignObjectSize + 8
          }
          x={
            isTopPosition
              ? sourceX - foreignObjectSize / 2
              : edgeCenterX - foreignObjectSize / 2
          }
          y={
            isTopPosition
              ? sourceY + foreignObjectSize / 4 - 8
              : edgeCenterY - (foreignObjectSize + 8) / 2 - 8
          }
          className="edgebutton-foreignobject"
          requiredExtensions="http://www.w3.org/1999/xhtml"
          style={{ paddingTop: 8 }}
        >
          {isPlatformVerify && !isActive && !isPreview && isMaxFlowVerify && (
            <div className={classes.root}>
              <div className={classes.btn} onClick={onShow} />
              <div
                ref={selectBlockRef}
                className={classnames(classes.selectBlock, {
                  [classes.none]: !isShowSelect,
                })}
              >
                <div className={classes.triangle} />
                <div className={classes.select}>
                  <Item
                    ref={el => {
                      subMenuRef.current[0] = el;
                    }}
                    text={t('item0')}
                    index={0}
                    showSubMenu={isShowSubMenuIndex === 0}
                    setShowSubMenu={(value, index) => {
                      setIsShowSubMenuIndex(value ? index : null);
                    }}
                    menuItem={[
                      { text: t('add-message'), onClick: onAddMessage },
                      {
                        text: t('add-coupon'),
                        onClick: onAddCoupon,
                        disabled: !couponList.length,
                      },
                    ]}
                  />
                  <Item
                    ref={el => {
                      subMenuRef.current[1] = el;
                    }}
                    text={t('item1')}
                    index={1}
                    showSubMenu={isShowSubMenuIndex === 1}
                    setShowSubMenu={(value, index) => {
                      setIsShowSubMenuIndex(value ? index : null);
                    }}
                    menuItem={[
                      {
                        text: t('tag-node'),
                        onClick: onAddTag,
                        disabled: targetNodeIsTag || isTag,
                      },
                      {
                        text: t('inbox-node'),
                        onClick: onAddInbox,
                        disabled: targetNodeIsInbox || isInbox,
                      },
                      {
                        text: t('add-ma'),
                        onClick: onAddMA,
                        disabled: !(
                          (isMessage ||
                            isCondition ||
                            isGACondition ||
                            isTag ||
                            isInbox) &&
                          targetNodeIsEnd &&
                          !!(list || []).length
                        ),
                      },
                    ]}
                  />
                  <Item
                    ref={el => {
                      subMenuRef.current[2] = el;
                    }}
                    text={t('item2')}
                    index={2}
                    showSubMenu={isShowSubMenuIndex === 2}
                    setShowSubMenu={(value, index) => {
                      setIsShowSubMenuIndex(value ? index : null);
                    }}
                    menuItem={[
                      {
                        text: t('add-conditions'),
                        onClick: onAddConditions,
                        /* 1. 只有上方node是發送訊息卡片且卡片按鈕支援分支 */
                        /* 2. line支援貼圖觸發，且需開通該功能（enable） */
                        /* 3. 所有平台都支援關鍵字條件（作為fallback） */
                        disabled: !(
                          !targetNodeIsCondition && isMaxEndNodVerify
                        ),
                      },
                      {
                        text: t('add-GAConditions'),
                        onClick: onAddGAConditions,
                        /* 只有line livechat支援ga4事件且需開通ga4Event功能 */
                        /* 不支援ga4分支接ga4分支，所以上方node不可為GAConditions node */
                        disabled: !(
                          (isLivechat || isLine) &&
                          checkFeatures(serviceFeatures, 'ga4Event') &&
                          !targetNodeIsGACondition &&
                          isMaxEndNodVerify
                        ),
                      },
                      {
                        text: t('add-customerConditions'),
                        onClick: onAddCustomerConditions,
                        disabled: !isMaxEndNodVerify,
                      },
                    ]}
                  />
                  <Item
                    ref={el => {
                      subMenuRef.current[3] = el;
                    }}
                    text={t('item3')}
                    index={3}
                    showSubMenu={isShowSubMenuIndex === 3}
                    setShowSubMenu={(value, index) => {
                      setIsShowSubMenuIndex(value ? index : null);
                    }}
                    menuItem={[
                      {
                        text: t('schedule'),
                        onClick: onAddSchedule,
                      },
                      {
                        text: t('wait'),
                        onClick: onAddWait,
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          )}
        </foreignObject>
      </ClickAwayListener>
    </>
  );
};

export default AddEdge;
